import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { minAsset } from 'app/layout/common/notifications/notifications.types';
import { MinUserObj } from 'app/models/document';
import { Conversation } from 'app/models/notification';
import { Util } from 'app/models/util';
import { EventService } from 'app/services/event.service';
import { NotificationService } from 'app/services/notification.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { StopMenuCloseDirective } from '@fuse/directives/stopMenuClose.directive';
import { getFullName } from '@fuse/pipes/full-name.pipe';


@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notification.component.scss'],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    imports: [MatButtonModule,
        NgIf, MatIconModule, MatMenuModule, StopMenuCloseDirective, MatTooltipModule, NgFor, NgClass, NgTemplateOutlet, RouterLink, DatePipe],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Conversation[];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    haveUnreadMessages: boolean = false;
    private subscription = new Subscription();

    // conversation : Conversation[] = [];

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        public eventService: EventService,
        private router: Router,
        private notifyServ: NotificationService,
        public util: Util,
        private toastr: ToastrService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.getUnreadMessages();
        this._changeDetectorRef.markForCheck();

        this.router.events?.subscribe((val) => {
            let user = this.util.getUserFromSession();
            if (user && user.id) {
                if (val instanceof NavigationEnd) this.getUnreadMessages();
            }
        });

        //subscribed to on/off notification bullet on chat icon . Off only when eventService checkUnreadMessages emit value 'off'
        //apart from router event subscription , this subscription is for tab change events in contact & message menu
        this.subscribeMessageNotifier();

        // // Subscribe to notification changes
        // this._notificationsService.notifications$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((notifications: Notification[]) =>
        //     {
        //         // Load the notifications
        //         this.notifications = notifications;

        //         // Calculate the unread count
        //         this._calculateUnreadCount();

        //         // Mark for check
        //         this._changeDetectorRef.markForCheck();
        //     });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        // notificationsComponent = null;
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    private subscribeMessageNotifier() {
        this.subscription.add(
            this.eventService.checkUnreadMessages.subscribe((data) => {
                this.getUnreadMessages();
            })
        );
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        this.notifications.forEach((notification, index) => {
            this.notifications[index].alreadyViewed = true;
        });
        this.allRead();

    }

    allRead() {
        this.notifyServ.markConversationReadAll().then((res) => {
            this.unreadCount = 0;
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Conversation): void {
        // Toggle the read status
        notification.alreadyViewed = !notification.alreadyViewed;

        this.markRead(notification.id);

    }

    markRead(id) {
        this.notifyServ.markConversationRead(id).then((res) => {
            --this.unreadCount;
        }).catch((error) => {
            false
            console.log(error);
        })
    }

    /**
     * Delete the given notification
     */
    deleteMark(notification, index): void {
        // Delete the notification
        this.notifyServ.deleteRecord(notification.id).then((res) => {
            //this.notifications.splice(index, 1);
            this.getUnreadMessages();
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(notification => !notification.alreadyViewed).length;
        }

        this.unreadCount = count;
    }

   public getUnreadMessages() {
        this.notifyServ
            .fetchUnreadMessage()
            .then((res) => {
                this.notifications = this.notifyServ.getData(res);
                this.notifications.map((notification) => {
                    notification.ui_title = this.getNotificationTypeDesc(notification);
                    notification.ui_icon = this.getNotificationTypeIcon(notification.type);
                });
                this._calculateUnreadCount();
                // if (data && data.length > 0 && !data[0].alreadyViewed) {
                // 	this.haveUnreadMessages = true;
                // }
            })
            .catch((err) => {
                console.log(err);
            });
    }

    getNotificationTypeIcon(notification) {
        let notification_icon = "bg-green-100";
        switch (notification) {
            //matter
            case "ITEM_MOVED":
                notification_icon = "mat_outline:drive_file_move";
                break;
            case "ITEM_DOWNLOADED":
                notification_icon = "heroicons_mini:arrow-path";
                break;
            case "ITEM_SHARED":
                notification_icon = "mat_outline:share";
                break;
            case "ITEM_UPLOADED":
                notification_icon = "feather:upload";
                break;
            case "ITEM_RENAMED":
                notification_icon = "mat_outline:edit";
                break;
            case "ITEM_TRASHED":
                notification_icon = "mat_outline:delete";
                break;
            case "ITEM_RESTORED":
                notification_icon = "mat_outline:restore";
                break;
            case "ITEM_DELETED":
                notification_icon = "mat_outline:delete";
                break;
            case "ITEM_UPDATED":
                notification_icon = "mat_outline:edit";
                break;
            case "ITEM_DUPLICATED":
                notification_icon = "mat_outline:file_copy";
                break;
            case "MARKED_FAVOURITE":
                notification_icon = "feather:star";
                break;
            case "UN_MARKED_FAVOURITE":
                notification_icon = "feather:star";
                break;
            case "NOTE_ADDED":
                notification_icon = "mat_outline:notes";
                break;
            case "NOTE_EDITED":
                notification_icon = "mat_outline:notes";
                break;
            case "NOTE_DELETED":
                notification_icon = "mat_outline:notes";
                break;
            case "LABEL_ADDED":
                notification_icon = "feather:tag";
                break;
            case "LABEL_UPDATED":
                notification_icon = "feather:tag";
                break;
            case "LABEL_REMOVED":
                notification_icon = "feather:tag";
                break;
            case "FOLDER_CREATED":
                notification_icon = "heroicons_solid:folder-plus";
                break;
            case "ACCESS_CHANGED_TO_VIEWER":
                notification_icon = "mat_outline:admin_panel_settings";
                break;
            case "ACCESS_CHANGED_TO_EDITOR":
                notification_icon = "mat_outline:admin_panel_settings";
                break;
            case "ACCESS_CHANGED_TO_ADMIN":
                notification_icon = "mat_outline:admin_panel_settings";
                break;
            case "ACCESS_CHANGED_TO_SUPERADMIN":
                notification_icon = "mat_outline:admin_panel_settings";
                break;
            case "ITEM_ACCESS_REMOVED":
                notification_icon = "mat_outline:delete";
                break;
            case "LOCK_ACQUIRED":
                notification_icon = "mat_outline:lock";
                break;
            case "LOCK_RELEASED":
                notification_icon = "mat_outline:lock_open";
                break;
            case "ITEM_SHARED_EXTERNALLY":
                notification_icon = "mat_outline:share_external";
                break;
                default:
                notification_icon = "heroicons_outline:bell";
                break;
        }
        return notification_icon;
    }

    getMessage(notification: Conversation) {
        return `<strong>${getFullName(notification.createdBy)}</strong> <em>${notification.description}</em>`
    }

    getNotificationTypeDesc(notification){
        const notification_type = notification.type;
        const asset:minAsset = notification.asset;
        const createdBy:MinUserObj = notification.createdBy;
        const createdByName = getFullName(createdBy);
        let title = notification.title;
        if(asset)
        switch (notification_type) {
            //matter
            case "ITEM_MOVED":
                title = `${createdByName} moved the ${asset?.type.toLowerCase()} "${asset?.name}"`
                break;
            case "ITEM_DOWNLOADED":
                title = `${createdByName} downloaded the ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "ITEM_SHARED":
                title = `${createdByName} shared the ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "ITEM_UPLOADED":
                title = `${createdByName} uploaded the ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "ITEM_RENAMED":
                title = `${createdByName} changed name of ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "ITEM_TRASHED":
                title = `${createdByName} moved the the ${asset?.type.toLowerCase()} ${asset?.name} to trash`
                break;
            case "ITEM_RESTORED":
                title = `${createdByName} restored the ${asset?.type.toLowerCase()} ${asset?.name} from trash`
                break;
            case "ITEM_DELETED":
                title = `${createdByName} deleted the ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "ITEM_UPDATED":
                title = `${createdByName} updated the ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "ITEM_DUPLICATED":
                title = `${createdByName} made a duplicate copy of ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "MARKED_FAVOURITE":
                title = `${createdByName} marked the ${asset?.type.toLowerCase()} ${asset?.name} as favorite`
                break;
            case "UN_MARKED_FAVOURITE":
                title = `${createdByName} removed the ${asset?.type.toLowerCase()} ${asset?.name} from favorites` 
                break;
            case "NOTE_ADDED":
                title = `${createdByName} added note to ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "NOTE_EDITED":
                title = `${createdByName} edited note of ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "NOTE_DELETED":
                title = `${createdByName} deleted note from ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "LABEL_ADDED":
                title = `${createdByName} added label to ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "LABEL_UPDATED":
                title = `${createdByName} updated label of ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "LABEL_REMOVED":
                title = `${createdByName} removed the label from ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "FOLDER_CREATED":
                title = `${createdByName} created the ${asset?.name} ${asset?.type.toLowerCase()}`
                break;
            case "ACCESS_CHANGED_TO_VIEWER":
                title = `${createdByName} changed the access of ${asset?.type.toLowerCase()} ${asset?.name} as super viewer`
                break;
            case "ACCESS_CHANGED_TO_EDITOR":
                title = `${createdByName} changed the access of ${asset?.type.toLowerCase()} ${asset?.name} as editor`
                break;
            case "ACCESS_CHANGED_TO_ADMIN":
                title = `${createdByName} changed the access of ${asset?.type.toLowerCase()} ${asset?.name} as admin`
                break;
            case "ACCESS_CHANGED_TO_SUPERADMIN":
                title = `${createdByName} changed the access of ${asset?.type.toLowerCase()} ${asset?.name} as super admin`
                break;
            case "ITEM_ACCESS_REMOVED":
                title = `${createdByName} removed the access from the ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "LOCK_ACQUIRED":
                title = `${createdByName} locked the ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            case "LOCK_RELEASED":
                title = `${createdByName} released the locked ${asset?.type.toLowerCase()} ${asset?.name}`
                break;
            default:
                break;
        }

        return title
    }
}
